import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Ban,
  Check,
  FileUp,
  Loader2,
  Package,
  Plus,
  Sparkles,
  Wand2,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";

// ★ FSD:features 互不 import,跨 feature 的登录态只许经 `@/contexts` 这道过渡门(ADR-0013 F1)。
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/shared/lib/rbac";
import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import {
  acqService,
  markAcqListsStale,
  ICP_TENANT_SCOPE,
  type AcqIcpItem,
  type AcqIcpPage,
  type AcqIcpProposal,
  type AcqProduct,
  type AcqProductSummary,
  type AcqStrategyView,
  byConfidenceDesc,
} from "../api/acqService";
import { icpSourceLabel, monthDayText } from "../model/acqLabels";
import { withProduct } from "../model/useProductParam";
import { useTenantProducts } from "../model/useTenantProducts";
import { AcqShell } from "./AcqShell";
import { groupIcpByProduct, IcpFactBoard, scopeLabel } from "./components/IcpColumn";
import { errorToast, errorText } from "@/shared/lib/apiErrors";

/**
 * 我的生意 —— **一条待确认托盘 + 两栏**(契约 §15.3,0917 批次四重做)。
 *
 * ★ 这一页不是"资料页",是**评分的输入**:今日商机的 `fit` 维度就是照它打的,
 *   目录的推荐词也从它推。页面第一句话就把这件事说出来,不然用户不知道填它有什么用。
 * ★★ 上一版是三栏(我卖什么 / 卖给谁 / 不要谁)、每条事实一张大卡。老板第 3 条
 *   「卖给谁那里太长了就不好翻了」说的就是它:十来条事实 = 十来张卡,一屏放不下三条,
 *   而用户在这一页真正要做的只有一件事 —— **哪些还没点头**。所以:
 *   ① 待确认单独抽成顶部一条托盘(要做的事在最上面,不用去两栏里找虚线框);
 *   ② 「卖给谁」「不要谁」合成一张按产品分组的紧凑表,一条事实 = 一枚 chip;
 *   ③ 左栏产品列表从一张张卡改成紧凑列表,底部保留「按现在的产品重新匹配商机」。
 * ★ ICP / 排除项在服务端恒 internal(契约 §6):这是自家筛客标准,**不进对客检索**。
 * ★★ **KB 为 0 时推不出东西是常态不是故障**(实测 33 租户绝大多数 KB=0)。
 *   那种情况下页面必须明说「先传资料再来」,而不是给一个「没推出来,再试一次」——
 *   后者会让用户以为功能坏了,然后一遍遍点一个永远不会成功的按钮。
 */
export default function MyBusinessPage() {
  const [page, setPage] = useState<AcqIcpPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [strategy, setStrategy] = useState<AcqStrategyView | null>(null);
  /**
   * 每个产品手上有几条商机(契约 §16.7:左栏产品行是这一页的**脊柱**)。
   * ★ 只为 `productSummary` 这一格才拉一次目录 —— 它是这一页唯一说不出口的那个数
   *   (画像的两个数这一页自己就有,商机数在获客那边)。
   * ★ 拉不到**整格不显示**,不报 0:一个写着「商机 0」的产品行会被读成
   *   "这条产品线一条单子都没有",而真相可能只是这一口没通。
   */
  const [productSummary, setProductSummary] = useState<AcqProductSummary[] | null>(null);
  // ★ 一次就够:这个 hook 自己会去 GET /tenant/products,调两遍就是两次请求
  //   (products 与 nameOf 本来就是同一份数据的两个面)。
  const { products, loading: productsLoading, nameOf } = useTenantProducts();
  /**
   * 重判补绑只给 **TENANT_ADMIN**(契约 §14.9)。
   * ★ 不用 `isTenantManagerPlus`:那一档含经理,而这一下是**整租户的一次系统性补账**
   *   (一次几十上百张卡走一遍 LLM)。门在服务端,这里藏按钮只是不让人点一个必然 403 的东西。
   * ★ `effectiveRole` 不是 `user.role`:平台超管代客进来时后端把视图角色切成了 TENANT_ADMIN。
   */
  const { effectiveRole } = useAuth();
  const canRejudge = effectiveRole === ROLES.TENANT_ADMIN;
  const [rejudging, setRejudging] = useState(false);

  useEffect(() => {
    let alive = true;
    acqService.icp().then(
      (v) => alive && setPage(v),
      (e) => alive && setError(errorText(e, { fallbackTitle: "加载失败" }))
    );
    // 建议画像的第二路料:趋势卡的场景词(契约 §13.6)。拉不到就整块不出现 ——
    // 多数行业还没铸 007,那时 `available=false`,不该在这儿摆一块空的「建议」。
    void acqService.strategy().then(
      (s) => alive && setStrategy(s),
      () => undefined
    );
    // 左栏那一行「商机 K」的料。失败静默 —— 它是锦上添花,不该挡住这一页。
    void acqService.catalog().then(
      (v) => alive && setProductSummary(v.productSummary ?? null),
      () => undefined
    );
    return () => {
      alive = false;
    };
  }, []);

  const suggest = async () => {
    setBusy(true);
    try {
      setPage(await acqService.suggestIcp());
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "AI 没能从知识库推出画像" }));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (factIds: number[]) => {
    if (factIds.length === 0) return;
    setBusy(true);
    try {
      setPage(await acqService.confirmIcp(factIds));
      toast({ title: "已确认", description: "今天起按这个给商机打分" });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "确认失败" }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 「这条我不要」(`POST /acq/icp/reject`)。
   *
   * ★★ 它和「确认」是一对,缺一个待办就清不掉:一个只能说"是"的列表,用户按不下去的
   *   那几条会一直挂着,最后整条托盘被当装饰无视 —— 而它是这一页唯一的待办。
   * ★ `skipped` 照实报:服务端忽略掉的是**已经确认过**的 id(一枚误点的 ✗ 打不回已生效的
   *   那条),报「已忽略 3 条」而其实只动了 1 条,下次他就不信这个数了。
   */
  const reject = async (factIds: number[]) => {
    if (factIds.length === 0) return;
    setBusy(true);
    try {
      const r = await acqService.rejectIcp(factIds);
      setPage(r);
      /**
       * ★★ 判 `typeof === "number"`,**不判真值、不 `?? 0`**:`skipped` 为 `null` 的意思是
       *   「这一口没算这个数」,不是「算出来是 0」。读成 0 的话,后端哪天把这一格留空,
       *   面上就会冒出一句「0 条没动」—— 一个从来没统计过的量被说成统计出来是零。
       */
      const skipped = typeof r.skipped === "number" ? r.skipped : null;
      toast(
        skipped != null && skipped > 0
          ? {
              title: `已忽略 ${factIds.length - skipped} 条，${skipped} 条没动（不是待确认）`,
            }
          : {
              title: "已忽略",
              description: "它不会再出现在待确认里，也不用来打分",
            }
      );
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "没忽略成" }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 场景词一键提名(契约 §14.6 `POST /acq/icp/propose`,**免费**)。
   *
   * ★★ 提名**不等于生效**:服务端走 `facts.propose` 建的是一条 pending 事实,
   *   还得在这一页点「确认」才进打分。所以提示里那半句「去确认」不是客套 ——
   *   少了它,用户以为加完就生效了,而 fit 分一点没变,他会以为这一页坏了。
   * ★ 成功后用返回的整页替换本地状态:待确认那个数当场跟着变,是"真加上了"的证据。
   * ★ 0917 批次四:带 `productCode` —— 同一个词加到哪条产品线下面是两件事
   *   (「图形商标」挂在商标注册下是画像,挂在高企认定下毫无意义)。
   */
  const propose = async (
    kind: AcqIcpProposal["kind"],
    value: string,
    productCode: string | undefined,
    productLabel: string
  ) => {
    // ★ 面上那句话也用 trim 过的值:请求发的是 trim 过的(api 的唯一键是 sha256(trim(value))),
    //   toast 里却回显带空格的原串,下一步他在托盘里看到的又是另一个样子。
    const v = value.trim();
    setBusy(true);
    try {
      setPage(await acqService.icpPropose(kind, v, productCode));
      toast({
        title: "已提名，去确认",
        description: `「${v}」进了待确认（${productLabel}）—— 点头之后才用来打分`,
      });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "没提名上" }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 按现在的产品树把老卡重判一遍(契约 §14.9)。**不扣积分**。
   *
   * ★ 为什么这枚按钮在「我卖什么」这一栏底下:它要解决的问题就是产品栏与商机之间那条
   *   断掉的线 —— 批次二之前采回的卡 `matched_product_code` 全空,而 dedupe 在精判之前,
   *   再跑同一条线 raw 全被去重 ⇒ 老卡**永远绑不上产品**。用户在这一页刚看完自己的产品树,
   *   下一句话正好是"那把以前的商机也按它对一遍"。
   * ★ 提示里报 `scanned` 与 `updated` 两个数:前者说范围,后者说这一下值不值。
   */
  const rejudge = async () => {
    setRejudging(true);
    try {
      const r = await acqService.rejudgeOpportunities();
      // 今日 / 线索库手上那份卡的产品格与意向格已经旧了 —— 发一个信号让挂着的那页重取。
      markAcqListsStale();
      toast({
        title: `重判 ${r.scanned} 张，绑上 ${r.updated} 张`,
        description:
          r.unmatched > 0
            ? `还有 ${r.unmatched} 张和你的产品树对不上 —— 它们不是失败，是确实不属于任何一条产品线`
            : undefined,
      });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "没重判成，没扣分" }));
    } finally {
      setRejudging(false);
    }
  };

  // 队列顺序由前端保证(见 byConfidenceDesc 的注释):把握低的沉到后面等人亲自看。
  const icps = useMemo(() => [...(page?.icps ?? [])].sort(byConfidenceDesc), [page]);
  const exclusions = useMemo(() => [...(page?.exclusions ?? [])].sort(byConfidenceDesc), [page]);
  const all = useMemo(() => [...icps, ...exclusions], [icps, exclusions]);
  const pendingItems = useMemo(() => all.filter((i) => !i.confirmed), [all]);
  const pendingIds = pendingItems.map((i) => i.factId);
  const pendingCount = page?.pendingCount ?? pendingIds.length;
  const confirmedCount = all.length - pendingIds.length;
  // KB 一篇都没有 —— 这条路径要先于"空画像"判,因为它俩长得一样但**该说的话完全不同**。
  const kbEmpty = page != null && page.kbDocCount === 0;
  const icpGroups = useMemo(() => groupIcpByProduct(icps, nameOf), [icps, nameOf]);
  /**
   * 每个产品的画像两个数(契约 §16.7 左栏脊柱:已确认 N · 待确认 M)。
   * ★ 只数 `icps`(卖给谁),**不数 `exclusions`**:排除项在面上是固定的最后一组、
   *   不按产品分(同行 / 已签约对哪条产品线都成立),把它算进某个产品行会让两处的数对不上。
   * ★ 键是 `productCode`(`__tenant__` 不在产品树里,自然不会有行)。
   */
  const productFactCounts = useMemo(() => {
    const m = new Map<string, { confirmed: number; pending: number }>();
    for (const it of icps) {
      const code = it.productCode?.trim();
      if (!code || code === ICP_TENANT_SCOPE) continue;
      const cur = m.get(code) ?? { confirmed: 0, pending: 0 };
      if (it.confirmed) cur.confirmed += 1;
      else cur.pending += 1;
      m.set(code, cur);
    }
    return m;
  }, [icps]);
  /** 每个产品手上几条商机。**没拉到就是 null**,那一格整个不显示(不写 0)。 */
  const productOpenCounts = useMemo(() => {
    if (productSummary == null) return null;
    return new Map(productSummary.map((p) => [p.code, p.openCount]));
  }, [productSummary]);
  const sceneWords = strategy?.available ? (strategy.newSceneWords ?? []) : [];
  const lastSuggested = monthDayText(page?.lastSuggestedAt);
  /** 已经提过名的词 → 提到哪几个产品下(`kind:productCode`)。见 `SceneWordSuggestions`。 */
  const proposedIndex = useMemo(() => buildProposedIndex(all), [all]);
  /**
   * 被驳回过的词(`IcpPage.rejected`)—— 场景词 chip 的第三态。
   * ★ 键同样 **trim 后**取(与 `proposedIndex` 一条规矩):api 存的是 `trim(value)`。
   */
  const rejectedWords = useMemo(
    () => new Set((page?.rejected ?? []).map((r) => r.value.trim())),
    [page]
  );

  return (
    <AcqShell subtitle="你卖什么、卖给谁、不要谁 —— 今日商机的匹配分就是照这个打的">
      {/* ★ `max-w-4xl` 删掉了(契约 §13.6 要求撑满):两栏并排本来就要宽。 */}
      <div className="w-full space-y-4">
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* ── 首屏那一句 + 一键推断 ────────────────────────────────────── */}
        <section className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-xl border bg-card px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">你卖什么、卖给谁、不要谁。</p>
            <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
              确认后，精判按它给商机打分，推荐按它给你出关键词。
              {page != null && (
                <>
                  {" · "}
                  已确认 {confirmedCount} 条
                  {pendingCount > 0 && (
                    <span className="text-warning"> · 待确认 {pendingCount} 条</span>
                  )}
                  {/* ★ 「上次推断」(契约 §14.6):这一页唯一会随时间变坏的就是它 ——
                      一份半年前推的画像和刚推的长得一模一样,而 fit 分天天照着它打。
                      ★ 缺席整句不说(后端还没跟上 / 一条事实都没有),**不写「未知」**。 */}
                  {lastSuggested && <> · 上次推断 {lastSuggested}</>}
                </>
              )}
            </p>
          </div>
          {/* ★ 「全部确认」不在这儿了 —— 它跟着待确认托盘走(要确认的东西就在它旁边)。 */}
          {!kbEmpty && (
            <Button size="sm" onClick={() => void suggest()} disabled={busy}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {all.length > 0 ? "重新推断（免费）" : "一键推断（免费）"}
            </Button>
          )}
        </section>

        {page == null && !error && <Skeleton className="h-64 w-full rounded-xl" />}

        {/* ── 待确认托盘(契约 §15.3)───────────────────────────────────── */}
        {page != null && !kbEmpty && pendingItems.length > 0 && (
          <PendingTray
            items={pendingItems}
            nameOf={nameOf}
            busy={busy}
            onConfirm={confirm}
            onReject={reject}
          />
        )}

        {/* ── 知识库是空的:这是多数租户第一次进来会看到的画面,不是边角 ───────── */}
        {kbEmpty && (
          <div className="rounded-xl border border-dashed bg-card px-6 py-10 text-center">
            <FileUp className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-base font-bold">知识库里还没有东西可读</p>
            <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-muted-foreground">
              画像是 AI 从你自己的资料里读出来的：服务介绍、报价单、成交复盘、常见问题都行。
              一篇都没有的时候它推不出东西 ——
              <span className="font-semibold text-foreground">这不是功能坏了，是还没喂料。</span>
              先传几篇再回来，一次就够。
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button asChild size="sm">
                <Link to="/knowledge">
                  <FileUp className="h-4 w-4" />
                  去传资料
                </Link>
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link to="/products">先建产品树</Link>
              </Button>
            </div>
          </div>
        )}

        {/* ── 两栏:左边「我卖什么」窄,右边事实表宽(它才是要翻的那一栏)──────── */}
        {page != null && !kbEmpty && (
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.85fr)]">
            <ProductColumn
              products={products}
              loading={productsLoading}
              canRejudge={canRejudge}
              rejudging={rejudging}
              onRejudge={() => void rejudge()}
              factCounts={productFactCounts}
              openCounts={productOpenCounts}
            />
            <IcpFactBoard
              icpGroups={icpGroups}
              exclusions={exclusions}
              nameOf={nameOf}
              busy={busy}
              onConfirm={confirm}
              emptyText={
                all.length === 0
                  ? `还没推过。AI 会读知识库里的 ${page.kbDocCount} 篇资料和产品树，按产品推出画像 —— 点上面的「一键推断」。`
                  : "这一栏还是空的，重新推断一次试试。"
              }
            />
          </div>
        )}

        {/* ── 行业里新冒出来的说法(契约 §15.3 场景词区重做)──────────────── */}
        {sceneWords.length > 0 && page != null && !kbEmpty && (
          <SceneWordSuggestions
            words={sceneWords}
            products={products}
            busy={busy}
            proposedIndex={proposedIndex}
            rejectedWords={rejectedWords}
            onPropose={propose}
          />
        )}
      </div>
    </AcqShell>
  );
}

/**
 * 待确认托盘(契约 §15.3)——「今天在这一页要做的事」全在这一条里。
 *
 * ★ 为什么抽出来:上一版待确认是散在两栏里的虚线卡,要点头得先把两栏翻一遍;
 *   而「哪些还没生效」正是这一页唯一的待办。
 * ★ 一条 chip 上必须带**所属产品**:同一句话挂在「商标注册」下还是「不指定产品」下,
 *   决定的是精判给谁打分 —— 点头之前得知道自己批的是哪一档。
 * ★ 没有「✗ 不要」:后端目前只有 confirm 没有 reject(`AcqIcpController` 只开了
 *   page / suggest / propose / confirm 四口)。摆一枚点了什么也不发生的叉,
 *   比不摆更坏 —— 见交回报告里的偏离说明。
 */
function PendingTray({
  items,
  nameOf,
  busy,
  onConfirm,
  onReject,
}: {
  items: AcqIcpItem[];
  nameOf: (code: string | null | undefined) => string;
  busy: boolean;
  onConfirm: (ids: number[]) => void;
  onReject: (ids: number[]) => void;
}) {
  return (
    <section className="rounded-xl border border-warning/40 bg-warning/5 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-sm font-semibold text-warning">
          待确认 {items.length} 条
          <span className="ml-2 text-2xs font-normal text-muted-foreground">
            你点头之后才用来给商机打分
          </span>
        </p>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => onConfirm(items.map((i) => i.factId))}
        >
          <Check className="h-4 w-4" />
          全部确认（{items.length}）
        </Button>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span
            key={it.factId}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-warning/40 bg-background py-1 pl-2.5 pr-1 text-2xs"
          >
            {it.kind === "exclusion" && <Ban className="h-3 w-3 shrink-0 text-warning" />}
            <span className="max-w-[18rem] truncate font-semibold">{it.value}</span>
            <span className="shrink-0 text-muted-foreground">· {scopeLabel(it, nameOf)}</span>
            {/* ★ 「AI 推的 / 你加的」(契约 §16.6):托盘上这一格最要紧 —— 用户在这儿
                一条条点头,而"这是我自己刚加的"和"这是 AI 从资料里读的"该用两种态度看。
                ★ 缺席不出现(后端还没跟上),不回落成「AI 推的」。 */}
            {icpSourceLabel(it.source) && (
              <span className="shrink-0 text-muted-foreground">· {icpSourceLabel(it.source)}</span>
            )}
            <button
              type="button"
              aria-label={`确认「${it.value}」`}
              disabled={busy}
              onClick={() => onConfirm([it.factId])}
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-success hover:bg-success/15 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            {/* ★ ✗ 与 ✓ 是一对:只有 ✓ 的待办清不掉,剩下那几条会一直挂着。
                ★ 不做二次确认:它是**可再来一遍**的(重新推断 / 再提一次名),
                  而每天为一条不要的词点两下,比偶尔误点一次更贵。 */}
            <button
              type="button"
              aria-label={`不要「${it.value}」`}
              disabled={busy}
              onClick={() => onReject([it.factId])}
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/15 hover:text-destructive disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
    </section>
  );
}

/**
 * 左栏「我卖什么」= 产品树只读紧凑列表(`/tenant/products`)。
 * ★ 只读是有意的:产品树的主人是产品页,在这儿开一个第二入口,两处改同一棵树迟早打架。
 * ★ 0917 批次四:一张张大卡改成一条条 —— 十个产品的租户(中细软就是)原来要占满一屏。
 *   摘要一行截断,鼠标停上去才展开(看得到全文,但不占版面)。
 * ★ 空态**不是灰一块**:产品树空的时候,「卖给谁」按产品分组这件事整个不成立
 *   (实测 33 个租户 33 家产品树是空的)—— 所以这里给的是一条去建树的路。
 */
function ProductColumn({
  products,
  loading,
  canRejudge,
  rejudging,
  onRejudge,
  factCounts,
  openCounts,
}: {
  products: AcqProduct[];
  loading: boolean;
  /** 仅 TENANT_ADMIN(契约 §14.9)。非管理员**整枚按钮不渲染**,不摆一个灰着的。 */
  canRejudge: boolean;
  rejudging: boolean;
  onRejudge: () => void;
  /** `node_code` → 这个产品的画像两个数(契约 §16.7)。 */
  factCounts: Map<string, { confirmed: number; pending: number }>;
  /** `node_code` → 手上几条商机。**null = 这一口没拉到**,那一格整个不显示。 */
  openCounts: Map<string, number> | null;
}) {
  return (
    <section className="min-w-0 space-y-2">
      <h2 className="flex items-baseline gap-x-1.5 text-sm font-bold">
        <Package className="h-4 w-4 shrink-0 translate-y-0.5 text-muted-foreground" />
        我卖什么
        {products.length > 0 && (
          <span className="text-2xs font-normal text-muted-foreground">
            {products.length} 个 · 来自产品树，只读
          </span>
        )}
      </h2>
      {loading ? (
        <Skeleton className="h-24 w-full rounded-xl" />
      ) : products.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card px-3 py-6 text-center">
          <p className="text-xs leading-relaxed text-muted-foreground">
            产品树还没建。没有产品，画像只能挂在「不指定产品」上，商机也分不到具体产品线。
          </p>
          <Button asChild size="sm" variant="outline" className="mt-3">
            <Link to="/products">去建产品树</Link>
          </Button>
        </div>
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {products.map((p) => {
            const fc = factCounts.get(p.nodeCode);
            const open = openCounts?.get(p.nodeCode);
            return (
              <li key={p.nodeCode} className="group px-3 py-2">
                <p className="truncate text-xs font-semibold" title={p.name}>
                  {p.name}
                </p>
                {p.summary && (
                  // ★ 一行截断 + hover 展开:占一行的版面,给全文的信息。
                  <p
                    className="truncate text-2xs leading-relaxed text-muted-foreground group-hover:overflow-visible group-hover:whitespace-normal"
                    title={p.summary}
                  >
                    {p.summary}
                  </p>
                )}
                {/* ★★ 契约 §16.7:这一行是**脊柱** —— 一个产品在获客仓里的全部现状
                    (画像点了几条头、还欠几条、手上几条单子)加一条出口。
                    上一版这里只有名字和摘要,于是"按产品找商机"这件事在页面上无处下手
                    (老板:「我没有感觉明显的操作方式」)。
                    ★ 商机数**拉不到就不显示**,不写 0:写 0 会被读成"这条线一条单子都没有"。 */}
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-muted-foreground">
                  <span>已确认 {fc?.confirmed ?? 0}</span>
                  {(fc?.pending ?? 0) > 0 && (
                    <span className="font-semibold text-warning">待确认 {fc!.pending}</span>
                  )}
                  {typeof open === "number" && <span>商机 {open}</span>}
                  <Link
                    to={withProduct("/ai-huoke/discover", p.nodeCode)}
                    className="inline-flex items-center gap-0.5 font-semibold text-primary hover:underline"
                  >
                    找这个产品的商机
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </p>
              </li>
            );
          })}
        </ul>
      )}
      {/* ★ 重判补绑(契约 §14.9):产品树在上面,这一句在下面 —— 「按现在的产品重新匹配商机」
          接的正是用户刚看完产品树的那个念头。**免费**,所以按钮上不写价;
          它要修的是一条断线:老卡的产品格是空的,而 dedupe 在精判之前 ⇒ 重跑同一条线也补不上。 */}
      {canRejudge && (
        <div className="space-y-1.5 pt-1">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={rejudging}
            onClick={onRejudge}
          >
            {rejudging ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
            {rejudging ? "正在重判…" : "按现在的产品重新匹配商机"}
          </Button>
          <p className="text-2xs leading-relaxed text-muted-foreground">
            把以前采回来、还没绑上产品的老商机按现在的产品树对一遍。免费，不扣积分。
          </p>
        </div>
      )}
    </section>
  );
}

/** `值` → 已经提过的 `kind:productCode` 集合(`productCode` 缺省是 `__tenant__`)。 */
type ProposedIndex = Map<string, Set<string>>;

function buildProposedIndex(items: AcqIcpItem[]): ProposedIndex {
  const idx: ProposedIndex = new Map();
  for (const it of items) {
    const code = it.productCode?.trim() || ICP_TENANT_SCOPE;
    // ★ 键用 **trim 过的值**:api 的唯一键是 `sha256(trim(value))`(契约 §15.2),
    //   两端归一规则不一致的话,「商标起名 」提名成功了、面上那枚 chip 却永远不变
    //   「已提名 ✓」—— 用户会一遍遍重提同一个词,而每次都以为没加上。
    const key = it.value.trim();
    const set = idx.get(key) ?? new Set<string>();
    set.add(`${it.kind}:${code}`);
    idx.set(key, set);
  }
  return idx;
}

/**
 * 「行业里新冒出来的说法」(契约 §15.3 场景词区重做,老板第 4 条「我都有点看不懂」)。
 *
 * ★★ 上一版这里有两个问题,老板两句话各点了一个:
 *   ① **「只能加一个」**:两个按钮直接 `propose(kind, word)` 不带产品,后端唯一键是
 *     `(tenant, kind, name)` 而 `name` 恒 `__tenant__` ⇒ 连提三个词,库里只剩最后一个
 *     (契约 §15.1 的真根因)。现在点开先选产品 —— 不同产品各是一条。
 *   ② **说明句是黑话**:「从行业趋势里捞的新场景词」「落在整体上」对着用户说没人懂。
 *     改成人话:这些词是什么、加进去会发生什么、加完是不是就生效了。
 * ★ 提过的词**留在原位变成「已提名 ✓」**,不消失:消失了用户会以为自己点错了,
 *   而且他可能还想把同一个词加到第二条产品线下面。
 * ★ 两个动作都给:一个新说法既可能是"我要这类客户",也可能是"这类我不接" ——
 *   只给「加入画像」等于替他假设了方向,而「不要谁」那一栏恰恰是省钱的那栏。
 */
function SceneWordSuggestions({
  words,
  products,
  busy,
  proposedIndex,
  rejectedWords,
  onPropose,
}: {
  words: string[];
  products: AcqProduct[];
  busy: boolean;
  proposedIndex: ProposedIndex;
  /** 被驳回过的词(trim 后)。 */
  rejectedWords: Set<string>;
  onPropose: (
    kind: AcqIcpProposal["kind"],
    value: string,
    productCode: string | undefined,
    productLabel: string
  ) => void;
}) {
  return (
    <section className="rounded-xl border border-dashed bg-muted/30 px-4 py-3">
      <p className="text-sm font-bold">行业里新冒出来的说法</p>
      <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
        本周行业趋势里新出现的场景词。加进画像 = 以后按它给商机打分；加进排除 = 这类不接。
        先落到待确认，<span className="font-semibold text-foreground">你点头才生效</span>。
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {words.map((w) => (
          <SceneWordChip
            key={w}
            word={w}
            products={products}
            busy={busy}
            proposed={proposedIndex.get(w.trim())}
            rejected={rejectedWords.has(w.trim())}
            onPropose={onPropose}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * 一枚场景词的三态(0917 批次四补):
 *   ① 值在 icps/exclusions 里(trim 精确匹配)→ **已提名 ✓**(留在原位,不同产品还能再提);
 *   ② 值在 `rejected` 里 → **已忽略**灰态,两个动作都不渲染;
 *   ③ 其余 → 可提名。
 *
 * ★★ ①优先于②:同一个词可能在 A 产品下被驳回、又在 B 产品下提过名 —— 那时该说的是
 *   "你已经加过了",不是"你忽略过它"(后者会让他以为自己加的那条没生效)。
 * ★★ 第二态的两个动作**不渲染而不是禁用**:禁用态要靠 hover 才知道为什么点不动,
 *   而这枚 chip 上他能做的事本来就是零。灰着的那句话自己说清了原因。
 * ★ 灰了也**不从列表里拿掉**:拿掉的话下周同一个词又原样出现(趋势不会因为他点过 ✗ 就不出),
 *   他会以为系统没记住 —— 而"记住了"这件事只有留在原位灰着才看得出来。
 */
function SceneWordChip({
  word,
  products,
  busy,
  proposed,
  rejected,
  onPropose,
}: {
  word: string;
  products: AcqProduct[];
  busy: boolean;
  proposed?: Set<string>;
  rejected?: boolean;
  onPropose: (
    kind: AcqIcpProposal["kind"],
    value: string,
    productCode: string | undefined,
    productLabel: string
  ) => void;
}) {
  const done = (proposed?.size ?? 0) > 0;

  if (!done && rejected) {
    return (
      <div
        title="你之前忽略过这个词；换个说法可以再提"
        className="inline-flex max-w-full items-center gap-x-1.5 rounded-full border border-dashed bg-muted/40 py-1 pl-2.5 pr-2.5 text-2xs text-muted-foreground"
      >
        <span className="truncate line-through">{word}</span>
        <span className="inline-flex shrink-0 items-center gap-0.5">
          <X className="h-3 w-3" />
          已忽略
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-full border bg-background py-1 pl-2.5 pr-2 text-2xs",
        done && "border-success/40"
      )}
    >
      <span className="font-semibold">{word}</span>
      {done && (
        <span className="inline-flex items-center gap-0.5 font-semibold text-success">
          <Check className="h-3 w-3" />
          已提名
        </span>
      )}
      <ProductPickPopover
        word={word}
        kind="icp"
        label="加入画像"
        icon={<Plus className="h-3 w-3" />}
        className="text-primary"
        products={products}
        busy={busy}
        proposed={proposed}
        onPropose={onPropose}
      />
      <span className="text-muted-foreground">·</span>
      <ProductPickPopover
        word={word}
        kind="exclusion"
        label="加入排除"
        icon={<Ban className="h-3 w-3" />}
        className="text-muted-foreground hover:text-warning"
        products={products}
        busy={busy}
        proposed={proposed}
        onPropose={onPropose}
      />
    </div>
  );
}

/**
 * 「加到哪个产品下」的小浮层。
 * ★ 默认那一条是**「不指定产品」**(= 后端的 `__tenant__`),排在最上面:多数租户
 *   产品树是空的,让他先选一个不存在的产品才能加词是把路堵死。
 * ★ 已经提过的那一档**禁用并打勾**,不是从列表里拿掉:拿掉之后用户只会以为自己看花了眼,
 *   而"这条已经加过了"本身就是他想知道的答案。
 */
function ProductPickPopover({
  word,
  kind,
  label,
  icon,
  className,
  products,
  busy,
  proposed,
  onPropose,
}: {
  word: string;
  kind: AcqIcpProposal["kind"];
  label: string;
  icon: React.ReactNode;
  className?: string;
  products: AcqProduct[];
  busy: boolean;
  proposed?: Set<string>;
  onPropose: (
    kind: AcqIcpProposal["kind"],
    value: string,
    productCode: string | undefined,
    productLabel: string
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  const options = [
    { code: ICP_TENANT_SCOPE, name: "不指定产品" },
    ...products.map((p) => ({ code: p.nodeCode, name: p.name })),
  ];
  const pick = (code: string, name: string) => {
    setOpen(false);
    onPropose(kind, word, code === ICP_TENANT_SCOPE ? undefined : code, name);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={busy}
          className={cn(
            "inline-flex items-center gap-0.5 font-semibold hover:underline disabled:opacity-50",
            className
          )}
        >
          {icon}
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <p className="px-2 py-1.5 text-2xs text-muted-foreground">「{word}」加到哪个产品下？</p>
        <div className="max-h-64 overflow-y-auto">
          {options.map((o) => {
            const already = proposed?.has(`${kind}:${o.code}`) ?? false;
            return (
              <button
                key={o.code}
                type="button"
                disabled={already}
                onClick={() => pick(o.code, o.name)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
              >
                <span className="min-w-0 flex-1 truncate">{o.name}</span>
                {already && <Check className="h-3.5 w-3.5 shrink-0 text-success" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
